import {QuizIndexType, QuizType} from "@/types/quiz";
import {parseQuizCSV} from "@/lib/quizParser";
import Papa from "papaparse"

const PROD_SPREADSHEET_ID = "1La_vKjn-eb3oGForMfBZza_qAL4prOzV1H7Y5xXtUns"
const DEV_SPREADSHEET_ID = "1RgIfF0P1F0P2k_z0aWGSfFCj1GH5aBA10sixOSMkkgI"

const SPREADSHEET_ID = process.env.NEXT_PUBLIC_ENVIRONMENT === "prod"
  ? PROD_SPREADSHEET_ID
  : DEV_SPREADSHEET_ID

const SHEETS_BASE_URL = `https://docs.google.com/spreadsheets/d/${SPREADSHEET_ID}/export?format=csv`


let cachedQuizIndex: QuizIndexType[] | null = null
let cacheTimestamp: number | null = null

const getQuizIndex = async (): Promise<QuizIndexType[]> => {
  // If index is less than five minutes old, return cached version
  if (cachedQuizIndex && cacheTimestamp && (Date.now() - cacheTimestamp < 5 * 60 * 1000)) {
    return cachedQuizIndex
  }

  try {
    const res = await fetch(SHEETS_BASE_URL)
    if (!res.ok) {
      throw new Error(`Failed to fetch quiz index: ${res.status} ${res.statusText}`)
    }

    const text = await res.text()
    const csv = Papa.parse<string[]>(text, {
      skipEmptyLines: true,
    })
    if (csv.errors.length > 0) {
      throw new Error(`Failed to parse quiz index CSV: ${csv.errors[0].message}`)
    }

    const index = csv.data.slice(1).map(row => ({
      slug: row[0],
      gid: row[1]
    }))

    cachedQuizIndex = index
    cacheTimestamp = Date.now()

    return cachedQuizIndex
  } catch (error) {
    // Don't cache bad data. Fall back to a stale cache if we have one so a
    // transient Sheets failure doesn't take down every quiz page.
    if (cachedQuizIndex) {
      console.error('Falling back to stale quiz index after fetch/parse error:', error)
      return cachedQuizIndex
    }
    throw error
  }
}

const cachedQuizes: {[slug: string]: {quiz: QuizType, cacheTimestamp: number}} = {}

const getQuizBySlug = async (slug: string): Promise<QuizType | null> => {
  if (cachedQuizes[slug] && (Date.now() - cachedQuizes[slug].cacheTimestamp < 5 * 60 * 1000)) {
    return cachedQuizes[slug].quiz
  }

  const index = await getQuizIndex()
  const quizEntry = index.find(entry => entry.slug === slug)
  if (!quizEntry) return null

  const res = await fetch(`${SHEETS_BASE_URL}&gid=${quizEntry.gid}`)
  if (!res.ok) {
    throw new Error(`Failed to fetch quiz "${slug}" (gid ${quizEntry.gid}): ${res.status} ${res.statusText}`)
  }
  const text = await res.text()

  const quiz = parseQuizCSV(text)
  cachedQuizes[slug] = { quiz, cacheTimestamp: Date.now() }
  return quiz
}

const PAGE_SIZE = 10

const getQuizzesByPage = async (page: number = 1): Promise<{ quizzes: Array<QuizType & { slug: string }>, total: number }> => {
  const index = await getQuizIndex()
  const start = (page - 1) * PAGE_SIZE
  const pageEntries = index.slice(start, start + PAGE_SIZE)

  const results = await Promise.all(
    pageEntries.map(async entry => {
      try {
        const quiz = await getQuizBySlug(entry.slug)
        return quiz ? { ...quiz, slug: entry.slug } : null
      } catch (e) {
        console.error(`Failed to load quiz "${entry.slug}":`, e)
        return null
      }
    })
  )

  return {
    quizzes: results.filter((q): q is QuizType & { slug: string } => q !== null),
    total: index.length
  }
}

const QuizService = {
  getQuizIndex,
  getQuizBySlug,
  getQuizzesByPage
}

export default QuizService